/**
 * Relatórios e Pauta no modo ao vivo, com dados reais (nada da simulação):
 *
 * - `list('briefings')`: os briefings dos checkpoints (09:00 … 23:30, fuso de São Paulo) montados com
 *   os pedidos reais da Kiwify e o gasto da Meta até a última hora cheia do checkpoint (às 23:30, até
 *   23:00: a Meta só dá o gasto por hora inteira), comparados com o mesmo
 *   horário do dia anterior. Faturamento = valor dos produtos (sem os juros do parcelamento). Sem
 *   fonte real ainda: briefing do expert (quiz), semanal, ações e blocos por papel ficam de fora.
 * - `getPautaObjections` (EXP-03): as objeções que o suporte marcou ao fechar itens da recuperação
 *   como perdidos (`GET /api/painel/recuperacao`), no período. Sem exemplo de frase: a nota é texto
 *   livre e pode ter o nome do cliente.
 *
 * Os ids dos briefings reais começam com `brf_real_`: a tela usa isso para não mostrar, no modo ao
 * vivo, um briefing que tenha chegado da simulação pelo stream.
 */
import type {
  Briefing,
  DateKey,
  KpiId,
  ListQuery,
  ObjectionTag,
  PautaObjections,
  Query,
} from '@rota/contracts';
import { fmt, fmtDate } from '@/lib/format';
import { addDaysKey, atTimeSP } from '@/lib/time';
import {
  DAY,
  dayStart,
  HOUR,
  matches,
  paginate,
  type RealCtx,
  type RealModule,
  type RealOrderRow,
  todayKey,
  totals,
} from './realCore';
import type { RealMedia } from './realMedia';
import { fetchRecoveryStates, type RecoveryStates } from './realRecovery';
import { isRealBriefing, REAL_BRIEFING_PREFIX } from './realReportsTypes';

export { isRealBriefing, REAL_BRIEFING_PREFIX };

/** Checkpoints padrão (SPEC 4.12) quando os Ajustes ainda não chegaram. */
const DEFAULT_CHECKPOINTS = ['09:00', '12:00', '15:00', '18:00', '21:00', '23:30'];

const LABEL: Partial<Record<KpiId, string>> = {
  revenueGross: 'Faturamento',
  frontSales: 'Vendas do curso',
  spendAm: 'Investimento na Meta',
};

const brl = (cents: number) => fmt(cents, 'brl0');
const int = (n: number) => fmt(n, 'int');

/** Variação relativa contra o mesmo horário do dia anterior (null sem base). */
const rel = (a: number, b: number | null): number | null =>
  b != null && b !== 0 ? (a - b) / Math.abs(b) : null;

function valueText(kpi: KpiId, v: number): string {
  return kpi === 'frontSales' ? int(v) : brl(v);
}

function numberLine(n: Briefing['numbers'][number], time: string, prevDay: string, bold: boolean): string {
  const v = valueText(n.kpi, n.value);
  // O gasto da Meta vai só até a última hora cheia (às 23:30, até 23:00): o texto diz até quando.
  const cut = n.kpi === 'spendAm' ? spendCutoff(time) : time;
  const label = n.kpi === 'spendAm' && cut !== time ? `${LABEL[n.kpi]} até ${cut}` : (LABEL[n.kpi] ?? n.kpi);
  const d = n.delta == null ? '' : ` (${fmt(n.delta, 'pct1', { sign: true })} vs ${prevDay} às ${cut})`;
  return `${label}: ${bold ? `*${v}*` : v}${d}`;
}

/** "Faturamento: +R$ 2.300 desde 12:00" para cada número que mudou desde o checkpoint anterior. */
function changesSince(prev: Briefing | null, numbers: Briefing['numbers'], since: string | null): string[] {
  if (!prev || !since) return [];
  const out: string[] = [];
  for (const n of numbers) {
    const p = prev.numbers.find((x) => x.kpi === n.kpi);
    if (!p || p.value === n.value) continue;
    const diff = n.value - p.value;
    const text =
      n.kpi === 'frontSales' ? `${diff > 0 ? '+' : ''}${int(diff)}` : fmt(diff, 'brl0', { sign: true });
    out.push(`${LABEL[n.kpi] ?? n.kpi}: ${text} desde ${since}`);
  }
  return out;
}

/**
 * Gasto da Meta de um dia até um instante (só onde há o detalhe por hora; senão, null). Conta só as
 * horas inteiras já fechadas: às 23:30, o gasto vai até 23:00 (a hora 23:00–24:00 ainda não acabou e
 * não dá para saber quanto dela caiu antes das 23:30). A tela diz "até 23:00" (`spendCutoff`).
 */
export function spendUntil(hourly: RealMedia['hourly'] | null, from: number, until: number): number | null {
  if (!hourly) return null;
  const day = hourly.filter((h) => h.t >= from && h.t < from + DAY);
  if (day.length === 0) return null;
  return day.filter((h) => h.t + HOUR <= until).reduce((s, h) => s + h.spendCents, 0);
}

/** Hora cheia até onde o gasto da Meta foi contado num checkpoint ("23:30" → "23:00"). */
export function spendCutoff(time: string): string {
  return `${time.slice(0, 2)}:00`;
}

export interface BriefingInputs {
  /** Dias (AAAA-MM-DD) do período pedido, em ordem. */
  days: readonly DateKey[];
  checkpoints: readonly string[];
  now: number;
  /** Pedidos reais desde o início do dia anterior ao primeiro dia (para a comparação). */
  orders: readonly RealOrderRow[];
  /** Gasto da Meta por hora (null sem a Meta). */
  hourly: RealMedia['hourly'] | null;
}

/**
 * Briefings dos checkpoints com os números reais: faturamento (valor dos produtos), vendas do curso e
 * o investimento na Meta até a hora do checkpoint, cada um contra o mesmo horário do dia anterior.
 */
export function buildCheckpointBriefings(i: BriefingInputs): Briefing[] {
  const out: Briefing[] = [];
  const slice = (from: number, until: number) =>
    totals(i.orders.filter((o) => o.at >= from && o.at < until) as RealOrderRow[]);
  for (const day of i.days) {
    const start = dayStart(day);
    let prev: Briefing | null = null;
    let prevTime: string | null = null;
    for (const time of [...i.checkpoints].sort()) {
      const at = atTimeSP(day, time);
      if (at > i.now) break;
      const t = slice(start, at);
      const y = slice(start - DAY, at - DAY);
      const spend = spendUntil(i.hourly, start, at);
      const spendY = spendUntil(i.hourly, start - DAY, at - DAY);
      const numbers: Briefing['numbers'] = [
        { kpi: 'revenueGross', value: t.gross, delta: rel(t.gross, y.gross) },
        { kpi: 'frontSales', value: t.front, delta: rel(t.front, y.front) },
        ...(spend != null ? [{ kpi: 'spendAm' as const, value: spend, delta: rel(spend, spendY) }] : []),
      ];
      const prevDay = day === todayKey(i.now) ? 'ontem' : 'o dia anterior';
      const upsell = t.upsell === 1 ? '1 do 20X' : `${int(t.upsell)} do 20X`;
      const front = t.front === 1 ? '1 venda do curso' : `${int(t.front)} vendas do curso`;
      const pace =
        t.sold === 0
          ? `Nenhum pedido pago até ${time}; ${prevDay} no mesmo horário, ${int(y.front)} do curso e ${int(y.upsell)} do 20X.`
          : `${front} e ${upsell} até ${time}; ${prevDay} no mesmo horário, ${int(y.front)} do curso e ${int(y.upsell)} do 20X.`;
      const headline = `Faturamento de ${brl(t.gross)} até ${time}`;
      const changes = changesSince(prev, numbers, prevTime);
      const label = `Briefing das ${time}`;
      const title = `${label} · ${fmtDate(day, 'long')}`;
      const body = ['', 'Ritmo do dia', pace];
      if (changes.length)
        body.push('', 'O que mudou desde o último briefing', ...changes.map((c) => `- ${c}`));
      const text = (bold: boolean) =>
        [title, '', ...numbers.map((n) => numberLine(n, time, prevDay, bold)), ...body].join('\n').trim();
      const b: Briefing = {
        id: `${REAL_BRIEFING_PREFIX}${day}_${time.replace(':', '')}`,
        at,
        kind: 'checkpoint',
        label,
        headline,
        numbers,
        pace,
        changes,
        actions: [],
        byRole: [],
        plainText: text(false),
        whatsappText: text(true),
      };
      out.push(b);
      prev = b;
      prevTime = time;
    }
  }
  return out.sort((a, b) => b.at - a.at);
}

/** Dias de um período (AAAA-MM-DD), sem passar de hoje. */
export function daysOf(from: DateKey, to: DateKey, today: DateKey): DateKey[] {
  const out: DateKey[] = [];
  for (let d = from; d <= to && d <= today && out.length < 120; d = addDaysKey(d, 1)) out.push(d);
  return out;
}

/**
 * Objeções da recuperação no período (EXP-03): itens fechados como perdidos com a objeção marcada,
 * pela data do fechamento. Sem exemplos de frase (a nota é texto livre).
 */
export function pautaObjections(states: RecoveryStates, since: number, until: number): PautaObjections {
  const counts = new Map<ObjectionTag, number>();
  let total = 0;
  for (const st of Object.values(states)) {
    const o = st.outcome;
    if (o?.result !== 'lost' || !o.objection || o.at < since || o.at > until) continue;
    total++;
    counts.set(o.objection, (counts.get(o.objection) ?? 0) + 1);
  }
  const rows = [...counts.entries()]
    .map(([tag, count]) => ({ tag, count, share: total ? count / total : 0 }))
    .sort((a, b) => b.count - a.count);
  return { since, until, total, rows, examples: [] };
}

export function reportsModule(
  ctx: RealCtx,
  fetchStates: () => Promise<RecoveryStates> = fetchRecoveryStates,
): RealModule {
  const { clock } = ctx;

  async function listBriefings(q: ListQuery) {
    const now = clock();
    const days = daysOf(q.range.from, q.range.to, todayKey(now));
    if (days.length === 0) return paginate([], q);
    const first = days[0] as DateKey;
    const from = dayStart(first) - DAY;
    const to = Math.min(now, dayStart(days[days.length - 1] as DateKey) + DAY);
    const toMin = Math.ceil(to / 60_000) * 60_000;
    const [rows, media] = await Promise.all([
      ctx.load(from, toMin),
      ctx.loadMedia(from, toMin).catch(() => null),
    ]);
    const briefings = buildCheckpointBriefings({
      days,
      checkpoints: ctx.settings()?.checkpoints ?? DEFAULT_CHECKPOINTS,
      now,
      orders: rows.filter((o) => matches(o, q)),
      hourly: media?.hourly ?? null,
    });
    return paginate(briefings, q);
  }

  async function getPautaObjections(q: Query): Promise<PautaObjections> {
    const now = clock();
    const since = dayStart(q.range.from);
    const until = Math.min(now, dayStart(q.range.to) + DAY - 1);
    const states = await fetchStates().catch((e: unknown) => {
      throw Object.assign(new Error(e instanceof Error ? e.message : 'Recuperação fora do ar'), {
        code: 'source_unavailable' as const,
      });
    });
    return pautaObjections(states, since, until);
  }

  return {
    overrides: { getPautaObjections },
    list: (kind, q) => (kind === 'briefings' ? listBriefings(q) : undefined),
  };
}
